import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { writeData } from "../../api";
import { getPageComponent } from "../../api/page/component";
import { ActionType, AppDispatch, RootState } from "../../reducer";
import { SetPageComponentsAction } from "../../reducer/note/component";
import { Component } from "../../types/component";
import { PageComponent } from "../../types/page/component";
import { useAppIdMap } from "../app";
import { useFetchComponentListener } from "../component";

export const useGetPageComponent = () => {
  const pageComponents = useSelector((state: RootState) => {
    return state.ui.pageComponents;
  });

  return (pageId: string | undefined) => {
    if (!pageId) return undefined;
    if (!(pageId in pageComponents)) return undefined;
    return pageComponents[pageId];
  };
};

export const useSetPageComponent = () => {
  const { appId } = useAppIdMap();
  const dispatch = useDispatch<AppDispatch>();
  const setPageComponentState = (config: {
    pageId: string | undefined;
    pageComponent: PageComponent;
  }) => {
    const { pageId, pageComponent } = config;
    if (!pageId) return;
    const action: SetPageComponentsAction = {
      pageComponents: { [pageId]: pageComponent },
      type: ActionType.SET_PAGE_COMPONENTS,
    };
    dispatch(action as any);
    return pageComponent;
  };

  const setPageComponent = async (config: {
    pageId: string | undefined;
    pageComponent: PageComponent;
  }) => {
    const { pageComponent, pageId } = config;
    if (!setPageComponentState(config)) return;
    return writeData(pageComponent);
  };

  const setPageComponentFromComponent = async (config: {
    pageId: string | undefined;
    component: Component;
  }) => {
    const { pageId, component } = config;
    if (!pageId) return;
    const pageComponent: PageComponent = {
      "app-id": appId!,
      "page-id": pageId,
      "component-app-id": component["app-id"],
      "component-id": component["component-id"],
      type: "omega/query-omega/page/component/PageComponent",
    };
    return setPageComponent({ pageId, pageComponent });
  };

  return {
    setPageComponent,
    setPageComponentFromComponent,
    setPageComponentState,
  };
};

export const usePageComponent = (config: { pageId: string | undefined }) => {
  const { pageId } = config;
  const getPageComponent = useGetPageComponent();
  const { setPageComponentFromComponent: setPageComponentSetter } =
    useSetPageComponent();
  const setPageComponent = (component: Component) =>
    setPageComponentSetter({ pageId, component });
  const pageComponent = getPageComponent(pageId);
  return { pageComponent, setPageComponent };
};

export const useFetchPageComponent = () => {
  const { appId } = useAppIdMap();
  const { setPageComponentState } = useSetPageComponent();
  return (config: { pageId: string | undefined }) => {
    const { pageId } = config;
    if (!pageId) return;
    getPageComponent({ "page-id": pageId, "app-id": appId! }).then(
      (pageComponent) => {
        setPageComponentState({ pageId, pageComponent });
      },
    );
  };
};

export const useFetchPageComponentListener = (pageId: string | undefined) => {
  const { pageComponent } = usePageComponent({ pageId });
  useFetchComponentListener(pageComponent?.["component-id"]);
  const fetchPageComponent = useFetchPageComponent();
  useEffect(() => {
    fetchPageComponent({ pageId });
  }, [pageId]);
};
